/**
 * Ordered read-back of a reply that is still being written.
 *
 * Chunks are synthesised concurrently and played strictly in sequence. Those
 * two facts are in tension and the whole class exists to hold them apart:
 * synthesis finishes out of order (a short chunk beats a long one sent before
 * it), so playback may never take "whatever is ready" — it takes `nextToPlay`
 * and waits if that one is not ready yet. Ordering is therefore a property of
 * the structure rather than of how fast the network happened to be.
 *
 * On the audio element: ONE element is reused for every chunk, and it is never
 * torn down here. iOS Safari grants autoplay per element, unlocked by a user
 * gesture — the first mic tap — and reassigning `.src` on an unlocked element
 * keeps the grant. Constructing a new Audio per chunk would lose it on the
 * second sentence, which is precisely the shape this queue would otherwise
 * take. The element is owned by the screen and outlives every queue.
 *
 * On interrupts: `stop()` latches the queue closed for good. A synthesis
 * already in flight cannot enqueue its result afterwards, and an `onended` from
 * the chunk that was playing cannot advance a queue that has been cleared. A
 * late response resolving into a stopped queue is the one way stale audio
 * reaches the speaker, and the latch is what makes that impossible rather than
 * unlikely.
 *
 * A queue is never revived — the screen builds a fresh one per read-back — so
 * `stopped` is the whole interrupt mechanism. An earlier version carried a
 * generation counter beside it, and every mutation of that counter left the
 * suite green: `stopped` was already refusing the same inputs. It was removed
 * rather than documented, because a second guard no test can tell apart from
 * the first only makes the first one look tested.
 */

export interface SpeechQueueDeps {
  /** One chunk of text to one audio blob. Must honour the abort signal. */
  synthesize: (text: string, signal: AbortSignal) => Promise<Blob>;
  /** The shared, gesture-unlocked audio element. Never replaced. */
  audio: HTMLAudioElement;
  createUrl?: (blob: Blob) => string;
  revokeUrl?: (url: string) => void;
  /** Raised when playback starts and when the queue goes quiet. */
  onSpeakingChange?: (speaking: boolean) => void;
  /**
   * Fires ONCE, after the last chunk of the reply has finished playing.
   *
   * This is what the listening-mode auto-restart hangs off. Per chunk it would
   * reopen the mic between sentences and record the agent speaking the rest of
   * its own reply.
   */
  onDrained?: () => void;
  onError?: (err: unknown) => void;
  /** Concurrent synthesis requests. Two keeps the queue ahead without a burst. */
  maxInFlight?: number;
}

type ItemStatus = 'pending' | 'synthesizing' | 'ready' | 'played' | 'failed';

interface QueueItem {
  seq: number;
  text: string;
  status: ItemStatus;
  url?: string;
  controller?: AbortController;
}

export class SpeechQueue {
  private items: QueueItem[] = [];
  private nextToPlay = 0;
  private playing = false;
  private finished = false;
  private drained = false;
  private stopped = false;
  private readonly maxInFlight: number;
  private readonly createUrl: (blob: Blob) => string;
  private readonly revokeUrl: (url: string) => void;

  constructor(private deps: SpeechQueueDeps) {
    this.maxInFlight = deps.maxInFlight ?? 2;
    this.createUrl = deps.createUrl ?? ((b) => URL.createObjectURL(b));
    this.revokeUrl = deps.revokeUrl ?? ((u) => URL.revokeObjectURL(u));
  }

  /** True while a chunk is playing or one is queued to play. */
  get isActive(): boolean {
    return !this.stopped && (this.playing || this.nextToPlay < this.items.length);
  }

  /** Add a chunk to the end of the queue. */
  enqueue(text: string): void {
    if (this.stopped || this.finished) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    this.items.push({ seq: this.items.length, text: trimmed, status: 'pending' });
    this.pump();
  }

  /**
   * No more chunks are coming.
   *
   * Separate from `enqueue` because the drain callback must not fire between
   * two sentences of the same reply — the queue draining and the REPLY ending
   * are different events, and only the screen knows the second one.
   */
  finish(): void {
    if (this.stopped) return;
    this.finished = true;
    this.maybeDrain();
  }

  /**
   * Abandon everything: pending synthesis, queued audio, and the chunk playing
   * right now. The audio element is paused, never discarded.
   */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;

    for (const item of this.items) {
      item.controller?.abort();
      if (item.url) {
        this.revokeUrl(item.url);
        item.url = undefined;
      }
    }
    /*
     * Emptying the list is half the interrupt mechanism; the `stopped` check in
     * the synthesis callback below is the other half. Either alone is
     * sufficient, so NEITHER IS INDIVIDUALLY TESTED — deleting one reddens
     * nothing, deleting both reddens all three stale-audio tests. The behaviour
     * is pinned; which line pins it is not, and no assertion from outside can
     * change that.
     *
     * Written down rather than tidied away, because the green you get from
     * deleting one of them reads exactly like "this guard has no detector".
     *
     * Two further copies WERE removed — `stopped` checks in the pump and before
     * playback — since they added a third and fourth spelling of the same rule
     * and nothing else. What `stopped` still covers alone is the pair of rules
     * only it can express: refusing new work after a stop, and ignoring the
     * `ended` event from the chunk this stop just paused.
     */
    this.items = [];
    this.nextToPlay = 0;

    const wasPlaying = this.playing;
    this.playing = false;

    try {
      this.deps.audio.pause();
      this.deps.audio.currentTime = 0;
    } catch {
      // A paused-before-load element throws on currentTime in some browsers.
    }

    if (wasPlaying) this.deps.onSpeakingChange?.(false);
  }

  /** Called by the owner when the shared element fires `ended`. */
  handleEnded(): void {
    if (this.stopped) return;

    const current = this.items[this.nextToPlay];
    if (current) {
      current.status = 'played';
      if (current.url) {
        this.revokeUrl(current.url);
        current.url = undefined;
      }
    }
    this.nextToPlay++;
    this.playing = false;
    this.pump();
    this.maybeDrain();
  }

  /** Called by the owner when the shared element fires `error`. */
  handleAudioError(): void {
    if (this.stopped) return;
    // Treat an unplayable chunk as played: the rest of the reply still runs,
    // and a stuck queue would leave the mic closed for good in listening mode.
    this.handleEnded();
  }

  private pump(): void {
    const inFlight = this.items.filter((i) => i.status === 'synthesizing').length;
    let budget = this.maxInFlight - inFlight;

    for (const item of this.items) {
      if (budget <= 0) break;
      if (item.status !== 'pending') continue;
      budget--;
      this.synthesizeItem(item);
    }

    this.playNextIfReady();
  }

  private synthesizeItem(item: QueueItem): void {
    const controller = new AbortController();
    item.controller = controller;
    item.status = 'synthesizing';

    this.deps
      .synthesize(item.text, controller.signal)
      .then((blob) => {
        // Not the audio guard — `stop()` already dropped this item from the
        // queue, so it can never play. This is here so a response landing after
        // an interrupt does not mint an object URL that nothing will revoke.
        if (this.stopped) return;
        item.url = this.createUrl(blob);
        item.status = 'ready';
        this.pump();
      })
      .catch((err) => {
        if (this.stopped) return;
        item.status = 'failed';
        this.deps.onError?.(err);
        // Skip it rather than stalling every later chunk behind it.
        if (item.seq === this.nextToPlay && !this.playing) {
          this.nextToPlay++;
          this.pump();
          this.maybeDrain();
        }
      });
  }

  private playNextIfReady(): void {
    if (this.playing) return;

    // Step over chunks that failed to synthesise, in order.
    while (this.items[this.nextToPlay]?.status === 'failed') {
      this.nextToPlay++;
    }

    const item = this.items[this.nextToPlay];
    // Not "the first ready item" — the NEXT one. Out-of-order synthesis waits.
    if (!item || item.status !== 'ready' || !item.url) return;

    this.playing = true;
    this.deps.onSpeakingChange?.(true);
    this.deps.audio.src = item.url;

    const play = this.deps.audio.play();
    if (play && typeof play.catch === 'function') {
      play.catch((err: unknown) => {
        if (this.stopped) return;
        this.deps.onError?.(err);
        this.handleEnded();
      });
    }
  }

  private maybeDrain(): void {
    if (this.drained || this.stopped || !this.finished) return;
    if (this.playing) return;
    if (this.nextToPlay < this.items.length) return;

    this.drained = true;
    this.deps.onSpeakingChange?.(false);
    this.deps.onDrained?.();
  }
}
